import { MobXProviderContext } from "mobx-react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { SelectButton } from "primereact/selectbutton";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getErrorTitle } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE } from "../../../constants/RegisterConstants";
import {
  appInnerCardStyle,
  appMutedTextStyle,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import DailyBehaviorLogPanel from "../../resident/components/DailyBehaviorLogPanel";
import Unauthorized from "../../../shared/pages/Unauthorized";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import { ensureCaregiverResidentOperationalAccess } from "../utils/caregiverResidentAccess";
import { canAccessCaregiverRole } from "../utils/caregiverRoleAccess";
import { buildBehaviorPrnMedicationOptions, buildBehaviorPrnSyncPayload } from "../../resident/utils/behaviorPrnUtils";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import { getLosAngelesDateKey } from "../../../utils/losAngelesDateTime";
import { sanitizeRichHtml } from "../../../utils/securityUtils";
import { buildBehaviorReportHtml } from "../../resident/utils/behaviorReportTemplate";
const CUSTOM_BEHAVIOR_KEY = "__custom_behavior__";

const getDefaultBehaviorReportMonth = () => getLosAngelesDateKey().slice(0, 7);

const getBehaviorReportMonthDateValue = (monthKey?: string) => {
  const normalizedMonthKey = String(monthKey || "").trim();
  if (!/^\d{4}-\d{2}$/.test(normalizedMonthKey)) {
    return null;
  }

  const [year, month] = normalizedMonthKey.split("-").map((value) => Number(value));
  return Number.isFinite(year) && Number.isFinite(month)
    ? new Date(Date.UTC(year, month - 1, 1, 12, 0, 0))
    : null;
};

const getBehaviorReportMonthKey = (value?: Date | Date[] | null) => {
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  if (!(resolvedValue instanceof Date) || Number.isNaN(resolvedValue.getTime())) {
    return "";
  }

  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
  });
  const parts = formatter.formatToParts(resolvedValue);
  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  return year && month ? `${year}-${month}` : "";
};

const BEHAVIOR_REPORT_RANGE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7Days" },
  { label: "Monthly", value: "monthly" },
] as const;

const getBehaviorWeekSummaryScope = (dataset: any) => String(dataset?.reportRangeKey || dataset?.reportMonth || "").trim();

const getBehaviorWeekSummaryKey = (dataset: any, packet: any, week: any) => (
  `${getBehaviorWeekSummaryScope(dataset)}-${Number(packet?.packetIndex || 0)}-${Number(week?.weekNumber || 0)}`
);

const buildBehaviorWeekSummaryDrafts = (dataset: any) => {
  const drafts: Record<string, string> = {};
  (dataset?.packets || []).forEach((packet: any) => {
    (packet?.weeks || []).forEach((week: any) => {
      drafts[getBehaviorWeekSummaryKey(dataset, packet, week)] = String(week?.summary || "");
    });
  });
  return drafts;
};

const applyBehaviorWeekSummaryDrafts = (dataset: any, drafts: Record<string, string>) => ({
  ...dataset,
  packets: (dataset?.packets || []).map((packet: any) => ({
    ...packet,
    weeks: (packet?.weeks || []).map((week: any) => ({
      ...week,
      summary: drafts[getBehaviorWeekSummaryKey(dataset, packet, week)] ?? week?.summary,
    })),
  })),
});

const canCaregiverEditBehaviorEntry = (entry: any) => {
  const recordedByRole = String(entry?.recordedByRole || "").trim().toLowerCase();
  if (recordedByRole !== "caregiver") {
    return false;
  }

  if (String(entry?.status || "").trim().toLowerCase() === "draft") {
    return true;
  }

  const entryDate = String(entry?.entryDate || "").trim() || getLosAngelesDateKey(entry?.startedAt);
  if (!entryDate) {
    return false;
  }

  return entryDate === getLosAngelesDateKey();
};

type CaregiverCbhsSection = "composer" | "recent" | "drafts" | "monthly";

const resolveCaregiverCbhsSectionFromQuery = (value?: string): CaregiverCbhsSection | null => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) {
    return null;
  }

  if (["new", "new-note", "new_note", "note", "composer"].includes(normalizedValue)) {
    return "composer";
  }

  if (["draft", "drafts", "draft-report", "draft-reports", "draft_reports"].includes(normalizedValue)) {
    return "drafts";
  }

  if (["recent", "recent-report", "recent-reports", "recent_reports"].includes(normalizedValue)) {
    return "recent";
  }

  if (["monthly", "report", "reports", "generate-reports"].includes(normalizedValue)) {
    return "monthly";
  }

  return null;
};

type CaregiverDailyBehaviorLogPageProps = {
  sharedMode?: boolean;
};

export const CaregiverDailyBehaviorLogPageContent = ({ sharedMode = false }: CaregiverDailyBehaviorLogPageProps) => {
  const { residentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const toast = useRef<Toast>(null);

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const canUseCaregiverWorkspace = sharedMode
    ? Boolean(userRoleType) || isAdminUser
    : canAccessCaregiverRole(authStore);
  const dashboardPath = sharedMode
    ? (userRoleType === ROLE_TYPE_MHP ? RouterAddress.MHP_HOME : RouterAddress.NURSE_HOME)
    : RouterAddress.CAREGIVER_HOME;
  const [loading, setLoading] = useState<boolean>(false);
  const [carePlanData, setCarePlanData] = useState<any>(null);
  const allowRecentEntryReuse = authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.enabled
    && !authExperience.featureFlags.dailyBehaviorLogReuseRecentEntries.excludedFacilityTypes.includes(
      String(carePlanData?.facilityType || authStore.getUserFacilityType() || "").trim().toLowerCase(),
    );
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false);
  const [accessBlockedMessage, setAccessBlockedMessage] = useState<string>("");
  const [behaviorLogEntries, setBehaviorLogEntries] = useState<any[]>([]);
  const [behaviorLogPage, setBehaviorLogPage] = useState<number>(1);
  const [behaviorLogPageTokens, setBehaviorLogPageTokens] = useState<string[]>([""]);
  const [behaviorLogNextToken, setBehaviorLogNextToken] = useState<string>("");
  const [loadingBehaviorLogEntries, setLoadingBehaviorLogEntries] = useState<boolean>(false);
  const [savingBehaviorLogEntry, setSavingBehaviorLogEntry] = useState<boolean>(false);
  const [loadingPrnMedicationOptions, setLoadingPrnMedicationOptions] = useState<boolean>(false);
  const [prnMedicationOptions, setPrnMedicationOptions] = useState<Array<{ label: string; value: string; medicationName?: string }>>([]);
  const [selectedBehaviorReportRange, setSelectedBehaviorReportRange] = useState<"today" | "last7Days" | "monthly">("monthly");
  const [selectedBehaviorReportMonth, setSelectedBehaviorReportMonth] = useState<string>(getDefaultBehaviorReportMonth());
  const [behaviorReportPreviewDataset, setBehaviorReportPreviewDataset] = useState<any>(null);
  const [loadingBehaviorReportPreview, setLoadingBehaviorReportPreview] = useState<boolean>(false);
  const [behaviorReportIncludesTierDetails, setBehaviorReportIncludesTierDetails] = useState<boolean>(true);
  const [behaviorHoursMode, setBehaviorHoursMode] = useState<"common" | "separate">("common");
  const [behaviorWeekSummaryDrafts, setBehaviorWeekSummaryDrafts] = useState<Record<string, string>>({});
  const [behaviorReportPreviewHtml, setBehaviorReportPreviewHtml] = useState<string>("");
  const [showBehaviorReportPreview, setShowBehaviorReportPreview] = useState<boolean>(false);
  const defaultActiveSection = resolveCaregiverCbhsSectionFromQuery(searchParams.get("section") || "") || "composer";
  const [activeSection, setActiveSection] = useState<CaregiverCbhsSection>(defaultActiveSection);

  useEffect(() => {
    setActiveSection(defaultActiveSection);
  }, [defaultActiveSection]);

  useEffect(() => {
    const currentSection = String(searchParams.get("section") || "").trim();
    if (currentSection === activeSection) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("section", activeSection);
    setSearchParams(nextSearchParams, { replace: true });
  }, [activeSection, searchParams, setSearchParams]);

  useEffect(() => {
    const loadResidentContext = async () => {
      if (!residentId || !canUseCaregiverWorkspace) {
        return;
      }

      setLoading(true);
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.getCaregiverResidentCarePlan(residentId, {
          behaviorLogContext: true,
        });
        setCarePlanData(response || null);
        setAccessBlockedMessage("");
      } catch (error: any) {
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setCarePlanData(null);
          setAccessBlockedMessage("Behavior Tracker is unavailable because the provider subscription is inactive.");
          setShowSubscriptionDialog(true);
        } else {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to load resident behavior details.",
            life: 4000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    void loadResidentContext();
  }, [backendAPIStore, canUseCaregiverWorkspace, residentId, userRoleType]);

  const reloadBehaviorLogEntries = async (options?: { page?: number; nextToken?: string; resetPagination?: boolean }) => {
    if (!residentId || !canUseCaregiverWorkspace) {
      return;
    }
    if (accessBlockedMessage) {
      return;
    }

    const targetPage = options?.resetPagination ? 1 : Math.max(1, options?.page || behaviorLogPage);
    const targetToken = options?.resetPagination ? "" : (options?.nextToken ?? behaviorLogPageTokens[targetPage - 1] ?? "");

    setLoadingBehaviorLogEntries(true);
    try {
      await backendAPIStore.init();
      const response = await backendAPIStore.getCaregiverResidentBehaviorLogEntriesPage(residentId, {
        limit: 10,
        nextToken: targetToken,
      });
      setBehaviorLogEntries(Array.isArray(response?.items) ? response.items : []);
      setBehaviorLogPage(targetPage);
      setBehaviorLogNextToken(String(response?.nextToken || ""));
      setBehaviorLogPageTokens((previousState) => {
        if (options?.resetPagination) {
          return [""];
        }
        const nextState = previousState.slice(0, targetPage);
        nextState[targetPage - 1] = targetToken;
        return nextState;
      });
    } catch (error: any) {
      if (error?.code === "SUBSCRIPTION_REQUIRED") {
        setAccessBlockedMessage("Behavior Tracker is unavailable because the provider subscription is inactive.");
        setShowSubscriptionDialog(true);
      } else {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load daily behavior log.",
          life: 4000,
        });
      }
    } finally {
      setLoadingBehaviorLogEntries(false);
    }
  };

  useEffect(() => {
    void reloadBehaviorLogEntries();
  }, [residentId, userRoleType, canUseCaregiverWorkspace]);

  useEffect(() => {
    const loadBehaviorReportPreview = async () => {
      if (!sharedMode || activeSection !== "monthly" || !residentId || !canUseCaregiverWorkspace) {
        setBehaviorReportPreviewDataset(null);
        return;
      }

      if (selectedBehaviorReportRange === "monthly" && !selectedBehaviorReportMonth) {
        setBehaviorReportPreviewDataset(null);
        return;
      }

      setLoadingBehaviorReportPreview(true);
      try {
        await backendAPIStore.init();
        const dataset = await backendAPIStore.getResidentBehaviorLogCbhsMonthlyDataset(residentId, {
          reportRange: selectedBehaviorReportRange,
          reportMonth: selectedBehaviorReportRange === "monthly" ? selectedBehaviorReportMonth : undefined,
          behaviorHoursMode: "common",
        });
        setBehaviorReportPreviewDataset(dataset || null);
        setBehaviorWeekSummaryDrafts(buildBehaviorWeekSummaryDrafts(dataset));
      } catch (error: any) {
        setBehaviorReportPreviewDataset(null);
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load the behavior report.",
          life: 4000,
        });
      } finally {
        setLoadingBehaviorReportPreview(false);
      }
    };

    void loadBehaviorReportPreview();
  }, [
    activeSection,
    backendAPIStore,
    canUseCaregiverWorkspace,
    residentId,
    selectedBehaviorReportMonth,
    selectedBehaviorReportRange,
    sharedMode,
    behaviorHoursMode,
  ]);

  useEffect(() => {
    const loadPrnMedicationOptions = async () => {
      if (!residentId || !canUseCaregiverWorkspace) {
        setPrnMedicationOptions([]);
        return;
      }
      setLoadingPrnMedicationOptions(true);
      try {
        const medicationResponse = await backendAPIStore.getResidentEmarMedications(residentId);
        setPrnMedicationOptions(buildBehaviorPrnMedicationOptions(medicationResponse?.items || medicationResponse || []));
      } catch {
        setPrnMedicationOptions([]);
      } finally {
        setLoadingPrnMedicationOptions(false);
      }
    };

    void loadPrnMedicationOptions();
  }, [backendAPIStore, canUseCaregiverWorkspace, residentId, userRoleType]);

  const behaviorOptions = useMemo(() => {
    const allRows = Array.isArray(carePlanData?.rows) ? carePlanData.rows : [];
    const optionMap = new Map<string, {
      behaviorKey: string;
      behaviorTitle: string;
      sectionCategory?: string;
      presentBehaviorSuggestions: string[];
      instructionSuggestions: string[];
      carePlanDetails?: string;
      caregiverInstructions?: string;
      carePlanInstructions?: string;
    }>();

    allRows
      .filter((row: any) => row?.sourceType === "behavior" && row?.itemKey)
      .forEach((row: any) => {
        const behaviorKey = String(row?.itemKey || "").trim();
        if (!behaviorKey) {
          return;
        }

        const existingOption = optionMap.get(behaviorKey);
        const presentBehaviorText = String(
          row?.description || row?.residentDetails || row?.present || "",
        ).trim();
        const instructionText = [
          String(row?.carePlanInstructions || "").trim(),
          String(row?.instructions || row?.caregiverInstructions || "").trim(),
        ].filter(Boolean).join("\n");

        if (!existingOption) {
          optionMap.set(behaviorKey, {
            behaviorKey,
            behaviorTitle: String(row?.itemLabel || "").trim() || "Behavior",
            sectionCategory: String(row?.sectionLabel || "").trim() || "Psych/Social & Cognitive",
            presentBehaviorSuggestions: presentBehaviorText ? [presentBehaviorText] : [],
            instructionSuggestions: instructionText ? [instructionText] : [],
            carePlanInstructions: String(row?.carePlanInstructions || "").trim(),
            carePlanDetails: presentBehaviorText,
            caregiverInstructions: instructionText,
          });
          return;
        }

        if (presentBehaviorText) {
          existingOption.presentBehaviorSuggestions.push(presentBehaviorText);
          if (!existingOption.carePlanDetails) {
            existingOption.carePlanDetails = presentBehaviorText;
          }
        }
        if (instructionText) {
          existingOption.instructionSuggestions.push(instructionText);
          if (!existingOption.caregiverInstructions) {
            existingOption.caregiverInstructions = instructionText;
          }
          if (row?.carePlanInstructions && !existingOption.carePlanInstructions) {
            existingOption.carePlanInstructions = String(row.carePlanInstructions).trim();
          }
        }
      });

    optionMap.forEach((option) => {
      const suggestions = option.instructionSuggestions
        .flatMap((value) => String(value || "").split(/\r?\n|[;]+/).map((item) => item.trim()).filter(Boolean));
      option.instructionSuggestions = [
        ...suggestions.filter((suggestion) => !/^Staff\b/i.test(suggestion)),
        ...suggestions.filter((suggestion) => /^Staff\b/i.test(suggestion)),
      ];
    });

    return [
      ...Array.from(optionMap.values()).sort((first, second) => first.behaviorTitle.localeCompare(second.behaviorTitle)),
      {
        behaviorKey: CUSTOM_BEHAVIOR_KEY,
        behaviorTitle: "Custom Behavior",
        sectionCategory: "Custom",
        presentBehaviorSuggestions: [],
        instructionSuggestions: [],
        carePlanDetails: "",
        caregiverInstructions: "",
      },
    ];
  }, [carePlanData]);

  const createBehaviorLogEntry = async (payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const latestAccess = await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      setCarePlanData((previousValue: any) => ({ ...(previousValue || {}), ...latestAccess }));
      setAccessBlockedMessage("");
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.createCaregiverResidentBehaviorLogEntry(residentId, resolvedPayload);
      await reloadBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry saved",
        detail: "Behavior tracker updated successfully.",
        life: 2500,
      });
      if (String(payload?.status || "").trim().toLowerCase() !== "draft") {
        if (sharedMode) {
          setActiveSection("recent");
        } else {
          navigate(dashboardPath);
        }
      }
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || "Behavior Tracker is not available right now.");
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const updateBehaviorLogEntry = async (behaviorLogEntryId: string, payload: Record<string, any>) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const latestAccess = await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      setCarePlanData((previousValue: any) => ({ ...(previousValue || {}), ...latestAccess }));
      setAccessBlockedMessage("");
      const resolvedPayload = await buildBehaviorPrnSyncPayload({
        backendAPIStore,
        residentId,
        behaviorPayload: payload,
      });
      await backendAPIStore.updateCaregiverResidentBehaviorLogEntry(residentId, {
        behaviorLogEntryId,
        ...resolvedPayload,
      });
      await reloadBehaviorLogEntries();
      toast.current?.show({
        severity: "success",
        summary: "Behavior entry updated",
        detail: "Behavior tracker updated successfully.",
        life: 2500,
      });
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || "Behavior Tracker is not available right now.");
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update behavior entry.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const deleteBehaviorLogDraft = async (behaviorLogEntryId: string) => {
    if (!residentId) {
      return;
    }

    setSavingBehaviorLogEntry(true);
    try {
      const latestAccess = await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      setCarePlanData((previousValue: any) => ({ ...(previousValue || {}), ...latestAccess }));
      setAccessBlockedMessage("");
      await backendAPIStore.deleteCaregiverResidentBehaviorLogEntry(residentId, behaviorLogEntryId);
      await reloadBehaviorLogEntries({ resetPagination: true });
      toast.current?.show({
        severity: "success",
        summary: "Draft deleted",
        detail: "Saved draft removed successfully.",
        life: 2500,
      });
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessBlockedMessage(error?.message || "Behavior Tracker is not available right now.");
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to delete draft.",
        life: 4000,
      });
      throw error;
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  const formatBehaviorLogText = async (requestBody: {
    text: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) => {
    try {
      return await backendAPIStore.formatAiText(requestBody);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to polish text with AI.",
        life: 4000,
      });
      throw error;
    }
  };

  const generateCaregiverInterventions = async (request: {
    behaviorTitle: string;
    behaviorCategory?: string;
    behaviorDetails?: string;
  }) => formatBehaviorLogText({
    text: "",
    taskType: "professional-note-cleanup",
    tone: "professional",
    domain: "behavior-caregiver-interventions",
    outputStyle: "plain_text",
    metadata: request,
  });

  const generateSharedBehaviorReport = async (outputMode: "preview" | "print") => {
    if (!residentId) {
      return;
    }
    setSavingBehaviorLogEntry(true);
    try {
      await backendAPIStore.init();
      const dataset = await backendAPIStore.getResidentBehaviorLogCbhsMonthlyDataset(residentId, {
        reportRange: selectedBehaviorReportRange,
        reportMonth: selectedBehaviorReportRange === "monthly" ? selectedBehaviorReportMonth : undefined,
        behaviorHoursMode,
      });
      const reportDataset = applyBehaviorWeekSummaryDrafts(dataset, behaviorWeekSummaryDrafts);
      const reportHtml = buildBehaviorReportHtml(
        reportDataset,
        {
          residentName: String(carePlanData?.residentName || "Resident").trim(),
          printedName: String(authStore.getUserName?.() || "").trim(),
          signatureDate: getLosAngelesDateKey(),
          includeTierDetails: behaviorReportIncludesTierDetails,
        },
      );
      if (outputMode === "preview") {
        setBehaviorReportPreviewHtml(reportHtml);
        setShowBehaviorReportPreview(true);
      } else {
        const printFrame = document.createElement("iframe");
        printFrame.style.position = "fixed";
        printFrame.style.right = "0";
        printFrame.style.bottom = "0";
        printFrame.style.width = "0";
        printFrame.style.height = "0";
        printFrame.style.border = "0";
        document.body.appendChild(printFrame);
        printFrame.onload = () => {
          window.setTimeout(() => {
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
          }, 50);
          window.setTimeout(() => {
            if (document.body.contains(printFrame)) {
              document.body.removeChild(printFrame);
            }
          }, 1500);
        };
        printFrame.srcdoc = reportHtml;
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the behavior report.",
        life: 4000,
      });
    } finally {
      setSavingBehaviorLogEntry(false);
    }
  };

  if (userRoleType && !canUseCaregiverWorkspace) {
    return <Unauthorized />;
  }

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <Dialog
        header={renderSurfaceHeader("Behavior Tracker Report", "Review the report before downloading.", "pi pi-file")}
        visible={showBehaviorReportPreview}
        modal
        maximizable
        style={{ width: "98vw", maxWidth: "98vw" }}
        contentStyle={{ height: "calc(100vh - 9rem)", padding: "0.75rem" }}
        onHide={() => setShowBehaviorReportPreview(false)}
        footer={(
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button type="button" label="Close" severity="secondary" outlined rounded onClick={() => setShowBehaviorReportPreview(false)} />
            <Button type="button" label="Print Report" icon="pi pi-print" severity="warning" rounded onClick={() => { void generateSharedBehaviorReport("print"); }} loading={savingBehaviorLogEntry} />
          </div>
        )}
      >
        <iframe
          title="Behavior Tracker report preview"
          srcDoc={behaviorReportPreviewHtml}
          style={{ width: "100%", height: "100%", minHeight: "72vh", border: "1px solid #e2e8f0", borderRadius: "12px", background: "#ffffff" }}
        />
      </Dialog>
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="Behavior Tracker"
        message="Behavior Tracker is unavailable because the provider subscription is inactive."
        showBillingButton={false}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div
          className="flex justify-content-between align-items-start gap-3 flex-wrap"
          style={{ width: "100%" }}
        >
          <div style={{ minWidth: 0 }}>
            {renderSurfaceHeader(
              `${carePlanData?.residentName || "Resident"} - Behavior Tracker`,
              "Write down behaviors, support given, and follow-up notes for the day.",
              "pi pi-clock",
            )}
          </div>
          <div className="flex align-items-center justify-content-end gap-2 flex-wrap">
            {sharedMode ? (
              <Button
                type="button"
                label="Generate Reports"
                severity="warning"
                rounded
                outlined
                onClick={() => setActiveSection("monthly")}
              />
            ) : null}
            <Button
              type="button"
              label="New Entry"
              severity="warning"
              rounded
              outlined
              onClick={() => setActiveSection("composer")}
            />
            <Button
              type="button"
              label="Draft Reports"
              severity="warning"
              rounded
              outlined
              onClick={() => setActiveSection("drafts")}
            />
            {sharedMode ? (
              <Button
                type="button"
                label="Recent Reports"
                severity="warning"
                rounded
                outlined
                onClick={() => setActiveSection("recent")}
              />
            ) : null}
          </div>
        </div>
        {loading ? (
          <div className="card flex justify-content-center p-5">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : accessBlockedMessage ? (
          <div style={appInnerCardStyle}>
            <div style={{ fontWeight: 800, color: "#0f172a" }}>Behavior Tracker</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {accessBlockedMessage}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ width: "100%", maxWidth: "72rem" }}>
          {sharedMode && activeSection === "monthly" ? (
            <>
            <div style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
              <div className="flex align-items-start justify-content-between gap-3 flex-wrap">
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Behavior Report</div>
                  <div className="mt-1" style={appMutedTextStyle}>
                    Choose a report range and view the Behavior Tracker report.
                  </div>
                </div>
                <div className="flex align-items-end gap-2 flex-wrap">
                  <label style={{ display: "grid", gap: "0.35rem", minWidth: "24rem" }}>
                    <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Report Range</span>
                    <SelectButton
                      value={selectedBehaviorReportRange}
                      onChange={(event) => setSelectedBehaviorReportRange(event.value)}
                      options={BEHAVIOR_REPORT_RANGE_OPTIONS as any}
                      optionLabel="label"
                      optionValue="value"
                      allowEmpty={false}
                    />
                  </label>
                  {selectedBehaviorReportRange === "monthly" ? (
                    <label style={{ display: "grid", gap: "0.35rem", minWidth: "15rem" }}>
                      <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.92rem" }}>Report Month</span>
                      <Calendar
                        value={getBehaviorReportMonthDateValue(selectedBehaviorReportMonth)}
                        onChange={(event) => setSelectedBehaviorReportMonth(getBehaviorReportMonthKey(event.value))}
                        view="month"
                        dateFormat="MM yy"
                        showIcon
                        readOnlyInput
                        className="w-full"
                        inputClassName="w-full"
                      />
                    </label>
                  ) : null}
                  <Button
                    type="button"
                    label="View Report"
                    icon="pi pi-eye"
                    severity="warning"
                    rounded
                    outlined
                    onClick={() => { void generateSharedBehaviorReport("preview"); }}
                    loading={savingBehaviorLogEntry}
                    disabled={loadingBehaviorReportPreview || !behaviorReportPreviewDataset || Number(behaviorReportPreviewDataset?.summary?.sourceEntryCount || 0) === 0}
                  />
                  <Button
                    type="button"
                    label="Print Report"
                    icon="pi pi-print"
                    severity="warning"
                    rounded
                    onClick={() => { void generateSharedBehaviorReport("print"); }}
                    loading={savingBehaviorLogEntry}
                    disabled={loadingBehaviorReportPreview || !behaviorReportPreviewDataset || Number(behaviorReportPreviewDataset?.summary?.sourceEntryCount || 0) === 0}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3" style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
              <div style={{ fontWeight: 800, color: "#0f172a" }}>Report Options</div>
              <div className="grid mt-2">
                <div className="col-12">
                  <div className="flex align-items-start gap-2">
                    <Checkbox
                      inputId="shared-behavior-report-tier-details"
                      checked={behaviorReportIncludesTierDetails}
                      onChange={(event) => setBehaviorReportIncludesTierDetails(Boolean(event.checked))}
                    />
                    <label htmlFor="shared-behavior-report-tier-details" style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}>
                      Include resident tier details in this report, as calculated by Hub.
                    </label>
                  </div>
                </div>
                <div className="col-12">
                  <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.6rem" }}>Multiple Behavior Hours</div>
                  <div className="flex flex-column gap-2">
                    <div className="flex align-items-start gap-2">
                      <Checkbox
                        inputId="shared-behavior-report-common-hours"
                        checked={behaviorHoursMode === "common"}
                        onChange={() => setBehaviorHoursMode("common")}
                      />
                      <label htmlFor="shared-behavior-report-common-hours" style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}>
                        Treat multiple behaviors as one common time (default).
                      </label>
                    </div>
                    <div className="flex align-items-start gap-2">
                      <Checkbox
                        inputId="shared-behavior-report-separate-hours"
                        checked={behaviorHoursMode === "separate"}
                        onChange={() => setBehaviorHoursMode("separate")}
                      />
                      <label htmlFor="shared-behavior-report-separate-hours" style={{ color: "#475569", lineHeight: 1.6, cursor: "pointer" }}>
                        Count hours separately for each behavior.
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-3" style={{ ...appInnerCardStyle, width: "100%", maxWidth: "56rem", margin: "0 auto" }}>
              <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                <div>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Weekly Summary</div>
                  <div className="mt-1" style={appMutedTextStyle}>
                    Review each week and update the summary before viewing the report.
                  </div>
                </div>
                {loadingBehaviorReportPreview ? <div style={appMutedTextStyle}>Refreshing report...</div> : null}
              </div>
              {behaviorReportPreviewDataset?.packets?.length ? (
                <div className="mt-3 flex flex-column gap-3">
                  {behaviorReportPreviewDataset.packets.map((packet: any) => (packet?.weeks || []).map((week: any) => {
                    const summaryKey = getBehaviorWeekSummaryKey(behaviorReportPreviewDataset, packet, week);
                    return (
                      <div key={summaryKey} style={{ padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #fed7aa", background: "#fffaf0" }}>
                        <div className="flex align-items-center justify-content-between gap-2 flex-wrap">
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>
                            Weekly {week?.weekNumber || ""} Summary: Note behaviors, unusual events, interventions, and whether they were successful.
                          </div>
                          <div style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                            {(week?.days || []).filter((day: any) => Array.isArray(day?.behaviorOccurrences) && day.behaviorOccurrences.some((occurrence: any) => Number(occurrence?.count || 0) > 0)).length} active day(s)
                          </div>
                        </div>
                        <div
                          className="mt-2"
                          contentEditable
                          suppressContentEditableWarning
                          onInput={(event) => setBehaviorWeekSummaryDrafts((previousState) => ({ ...previousState, [summaryKey]: sanitizeRichHtml(event.currentTarget.innerHTML) }))}
                          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(behaviorWeekSummaryDrafts[summaryKey] || "") }}
                          style={{ minHeight: "10rem", padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #fed7aa", background: "#ffffff", color: "#0f172a", lineHeight: 1.6, outline: "none" }}
                        />
                      </div>
                    );
                  }))}
                </div>
              ) : !loadingBehaviorReportPreview ? (
                <div className="mt-3" style={appMutedTextStyle}>No daily behavior entries were found for this report range.</div>
              ) : null}
            </div>
            </>
          ) : (
          <DailyBehaviorLogPanel
            behaviorOptions={behaviorOptions}
            entries={behaviorLogEntries}
            loading={loadingBehaviorLogEntries}
            saving={savingBehaviorLogEntry}
            emptyMessage="No caregiver behavior entries yet for this resident."
            collapsibleComposer
            defaultComposerVisible
            composerVisible={activeSection === "composer"}
            onComposerVisibleChange={() => setActiveSection("composer")}
            showComposerSection={activeSection === "composer"}
            showEntriesSection={activeSection === "recent"}
            hideEntriesSection={false}
            showDraftsSection={activeSection === "drafts"}
            showComposerToggleButton={false}
            serverEntriesPage={behaviorLogPage}
            serverEntriesPageSize={10}
            serverEntriesHasPreviousPage={behaviorLogPage > 1}
            serverEntriesHasNextPage={Boolean(behaviorLogNextToken)}
                onEntriesPreviousPage={() => {
                  const previousPage = Math.max(1, behaviorLogPage - 1);
                  void reloadBehaviorLogEntries({
                    page: previousPage,
                    nextToken: behaviorLogPageTokens[previousPage - 1] || "",
                  });
                }}
                onEntriesNextPage={() => {
                  if (!behaviorLogNextToken) {
                    return;
                  }
                  const nextPage = behaviorLogPage + 1;
                  setBehaviorLogPageTokens((previousState) => {
                    const nextState = previousState.slice(0, nextPage);
                    nextState[nextPage - 1] = behaviorLogNextToken;
                    return nextState;
                  });
                  void reloadBehaviorLogEntries({
                    page: nextPage,
                    nextToken: behaviorLogNextToken,
                  });
                }}
            canEditEntry={sharedMode ? () => true : canCaregiverEditBehaviorEntry}
            onFormatText={formatBehaviorLogText}
            onGenerateCaregiverInterventions={generateCaregiverInterventions}
            onCreateEntry={createBehaviorLogEntry}
            onUpdateEntry={updateBehaviorLogEntry}
            onDeleteDraft={deleteBehaviorLogDraft}
          onDraftSaved={() => setActiveSection("drafts")}
          prnMedicationOptions={prnMedicationOptions}
          loadingPrnMedicationOptions={loadingPrnMedicationOptions}
          allowRecentEntryReuse={allowRecentEntryReuse}
        />
          )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CaregiverDailyBehaviorLogPage = () => <CaregiverDailyBehaviorLogPageContent />;

export default CaregiverDailyBehaviorLogPage;
